import axios from "../redux/sagas/axios";
import { currencySymbols } from "./currency";

/**
 * Get user's effective currency data
 * @param {string} userId - User ID
 * @returns {Promise<Object>} User currency data with effective currency
 */
export const getUserCurrency = async (userId) => {
  try {
    const response = await axios.get(`/currency/user-currency/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user currency:", error);
    return null;
  }
};

/**
 * Set user's preferred currency
 * @param {string} userId - User ID
 * @param {string} currency - Currency code (e.g., "USD", "ZMW")
 * @param {string} adminToken - Admin authentication token
 * @returns {Promise<Object>} API response
 */
export const setUserCurrency = async (userId, currency, adminToken) => {
  try {
    const response = await axios.post(
      "/currency/set-user-currency",
      { userId, currency },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error setting user currency:", error);
    throw error;
  }
};

/**
 * Remove user's preferred currency (use system default)
 * @param {string} userId - User ID
 * @param {string} adminToken - Admin authentication token
 * @returns {Promise<Object>} API response
 */
export const removeUserCurrency = async (userId, adminToken) => {
  try {
    const response = await axios.delete(
      `/currency/remove-user-currency/${userId}`,
      {
        headers: { Authorization: `Bearer ${adminToken}` },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error removing user currency:", error);
    throw error;
  }
};

/**
 * Convert USD amount to user's preferred currency
 * @param {number} usdAmount - Amount in USD
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Converted amount and currency info
 */
export const convertToUserCurrency = async (usdAmount, userId) => {
  try {
    const userCurrencyData = await getUserCurrency(userId);
    const targetCurrency = userCurrencyData?.effectiveCurrency || "USD";

    if (targetCurrency === "USD") {
      return {
        amount: usdAmount,
        currency: "USD",
        symbol: currencySymbols["USD"],
        isUserPreferred: !!userCurrencyData?.preferredCurrency,
      };
    }

    const response = await axios.post("/currency/convert", {
      amount: usdAmount,
      fromCurrency: "USD",
      toCurrency: targetCurrency,
    });

    return {
      amount: response.data.convertedAmount,
      currency: targetCurrency,
      symbol: currencySymbols[targetCurrency] || "",
      isUserPreferred: !!userCurrencyData?.preferredCurrency,
      rate: response.data.rate,
      lastUpdated: response.data.lastUpdated,
    };
  } catch (error) {
    console.error("Error converting to user currency:", error);
    // Fallback to USD
    return {
      amount: usdAmount,
      currency: "USD",
      symbol: currencySymbols["USD"],
      isUserPreferred: false,
      error: true,
    };
  }
};

/**
 * Format amount with user's preferred currency
 * @param {number} usdAmount - Amount in USD
 * @param {string} userId - User ID
 * @returns {Promise<string>} Formatted currency string
 */
export const formatUserCurrency = async (usdAmount, userId) => {
  try {
    const { amount, currency, symbol } = await convertToUserCurrency(
      usdAmount,
      userId
    );
    return `${symbol}${amount.toFixed(2)} ${currency}`;
  } catch (error) {
    console.error("Error formatting user currency:", error);
    return `$${usdAmount.toFixed(2)} USD`;
  }
};

/**
 * Get currency priority explanation for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Priority explanation
 */
export const getCurrencyPriorityInfo = async (userId) => {
  try {
    const userCurrencyData = await getUserCurrency(userId);

    if (
      userCurrencyData?.preferredCurrency &&
      !userCurrencyData?.usingSystemDefault
    ) {
      return {
        currency: userCurrencyData.preferredCurrency,
        priority: "user",
        explanation: "Using user's preferred currency",
        icon: "👤",
      };
    } else if (userCurrencyData?.usingSystemDefault) {
      return {
        currency: userCurrencyData.effectiveCurrency,
        priority: "system",
        explanation: "Using system default currency",
        icon: "🌐",
      };
    } else {
      return {
        currency: "USD",
        priority: "fallback",
        explanation: "Using fallback currency (USD)",
        icon: "⚙️",
      };
    }
  } catch (error) {
    console.error("Error getting currency priority info:", error);
    return {
      currency: "USD",
      priority: "fallback",
      explanation: "Using fallback currency (USD)",
      icon: "⚙️",
      error: true,
    };
  }
};
